import { useEffect, useState } from 'react';
import type { Key } from 'react-aria-components';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import { useSearch } from '@backstage/plugin-search-react';
import { useApi } from '@backstage/core-plugin-api';
import { Select } from '@backstage/ui';
import { QUADRANTS, RINGS } from '@internal/backstage-plugin-tech-radar-common';
import styles from './search-tab-content.module.css';

const useSearchFilter = () => {
  const { filters, setFilters } = useSearch();
  const setFilter = (key: string, keys: Key | Key[] | null) => {
    let arr: Key[];
    if (Array.isArray(keys)) {
      arr = keys;
    } else if (keys) {
      arr = [keys];
    } else {
      arr = [];
    }
    setFilters(prev => ({
      ...prev,
      [key]: arr.length ? arr : undefined,
    }));
  };
  return { filters, setFilters, setFilter };
};

export const CatalogSearchFilters = () => {
  const { filters, setFilter } = useSearchFilter();

  return (
    <>
      <Select
        placeholder="Select kinds..."
        selectionMode="multiple"
        options={[
          { value: 'Component', label: 'Component' },
          { value: 'Template', label: 'Template' },
          { value: 'API', label: 'API' },
          { value: 'System', label: 'System' },
          { value: 'Group', label: 'Group' },
          { value: 'User', label: 'User' },
        ]}
        value={(filters.kind as string[]) ?? []}
        onChange={keys => setFilter('kind', keys)}
        className={styles.filterSelect}
      />
      <Select
        placeholder="Select lifecycles..."
        selectionMode="multiple"
        options={[
          { value: 'experimental', label: 'Experimental' },
          { value: 'production', label: 'Production' },
        ]}
        value={(filters.lifecycle as string[]) ?? []}
        onChange={keys => setFilter('lifecycle', keys)}
        className={styles.filterSelect}
      />
    </>
  );
};

export const TechDocsSearchFilters = () => {
  const { filters, setFilters } = useSearch();
  const catalogApi = useApi(catalogApiRef);
  const [options, setOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  useEffect(() => {
    catalogApi
      .getEntities({
        fields: ['metadata.name'],
        filter: {
          'metadata.annotations.backstage.io/techdocs-ref':
            CATALOG_FILTER_EXISTS,
        },
      })
      .then(({ items }) => {
        const names = items
          .map(e => e.metadata.name)
          .sort((a, b) => a.localeCompare(b));
        setOptions(names.map(n => ({ value: n, label: n })));
      });
  }, [catalogApi]);

  return (
    <Select
      placeholder="Select entity..."
      selectionMode="single"
      searchable
      options={options}
      value={(filters.name as string) ?? null}
      onChange={key =>
        setFilters(prev => ({ ...prev, name: key || undefined }))
      }
      className={styles.filterSelect}
    />
  );
};

export const TechRadarSearchFilters = () => {
  const { filters, setFilter } = useSearchFilter();

  return (
    <>
      <Select
        placeholder="Select categories..."
        selectionMode="multiple"
        options={QUADRANTS.map(quadrant => ({
          value: quadrant.id,
          label: quadrant.label,
        }))}
        value={(filters.category as string[]) ?? []}
        onChange={keys => setFilter('category', keys)}
        className={styles.filterSelect}
      />
      <Select
        placeholder="Select zones..."
        selectionMode="multiple"
        options={RINGS.map(ring => ({ value: ring.key, label: ring.name }))}
        value={(filters.zone as string[]) ?? []}
        onChange={keys => setFilter('zone', keys)}
        className={styles.filterSelect}
      />
    </>
  );
};
